<template>
  <v-dialog
    :value="value"
    max-width="640px"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card class="rounded-xl d-flex flex-column" style="max-height: 90vh">
      <!-- Title header -->
      <v-card-title class="primary white--text py-3 d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-calculator</v-icon>
        <div class="text-truncate">
          <span class="text-subtitle-1 text-sm-h6 font-weight-bold white--text"
            >GCS-laskuri</span
          >
          <span
            class="d-none d-sm-inline text-caption white--text text--lighten-3 ml-2"
          >
            (Glasgow Coma Scale)
          </span>
        </div>
        <v-spacer />
        <v-chip
          small
          color="white"
          text-color="primary"
          class="font-weight-bold mr-2"
        >
          {{ totalScoreText }}
        </v-chip>
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Step Navigation Bar / Wizard Steps -->
      <div :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'" class="px-2 py-2 border-bottom">
        <v-row no-gutters class="text-center">
          <v-col v-for="(st, idx) in steps" :key="idx" cols="3" class="px-1">
            <v-btn
              block
              small
              depressed
              :color="currentStep === idx + 1 ? 'primary' : 'white'"
              :class="[
                'rounded-pill text-none text-caption font-weight-bold',
                currentStep === idx + 1
                  ? 'white--text'
                  : 'grey--text text--darken-3',
              ]"
              @click="currentStep = idx + 1"
            >
              <span class="text-truncate">{{ st.short }}</span>
              <v-badge
                v-if="st.score !== null"
                inline
                small
                :content="`${st.score}p`"
                :color="
                  currentStep === idx + 1 ? 'red darken-4' : 'grey lighten-1'
                "
                class="ml-1"
              />
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Scrollable Step Content -->
      <v-card-text class="pt-4 px-3 px-sm-5 flex-grow-1 overflow-y-auto">
        <!-- Quick shortcut for fully alert patient -->
        <div
          v-if="currentStep < 4 && !isAllMax"
          class="mb-3 d-flex justify-end"
        >
          <v-btn
            x-small
            outlined
            color="primary"
            class="font-weight-bold rounded-pill text-none"
            @click="setAllMax"
          >
            <v-icon x-small left>mdi-flash</v-icon>
            Pika-asetus: Täysi vaste (GCS 15)
          </v-btn>
        </div>

        <!-- STEP 1: SILMÄT -->
        <div v-if="currentStep === 1">
          <div class="d-flex align-center mb-1">
            <v-avatar color="primary lighten-5" size="32" class="mr-2">
              <v-icon color="primary" small>mdi-eye</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                1/3 Silmät (SI) – Silmien avaaminen
              </div>
              <div class="text-caption grey--text text--darken-2">
                Miten potilas aukaisee silmänsä?
              </div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in gcsCriteria.eyes"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                eyesScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                eyesScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectEyes(opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    eyesScore === opt.score ? 'primary' : 'grey lighten-3'
                  "
                  :class="
                    eyesScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      eyesScore === opt.score ? 'primary--text' : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="eyesScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 2: PUHE -->
        <div v-else-if="currentStep === 2">
          <div class="d-flex align-center mb-1">
            <v-avatar color="primary lighten-5" size="32" class="mr-2">
              <v-icon color="primary" small>mdi-account-voice</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                2/3 Puhe (PU) – Kielellinen vaste
              </div>
              <div class="text-caption grey--text text--darken-2">
                Paras kielellinen vaste (kysy aikaa, paikkaa ja nimeä):
              </div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in gcsCriteria.verbal"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                verbalScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                verbalScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectVerbal(opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    verbalScore === opt.score ? 'primary' : 'grey lighten-3'
                  "
                  :class="
                    verbalScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      verbalScore === opt.score
                        ? 'primary--text'
                        : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="verbalScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 3: LIIKE -->
        <div v-else-if="currentStep === 3">
          <div class="d-flex align-center mb-1">
            <v-avatar color="primary lighten-5" size="32" class="mr-2">
              <v-icon color="primary" small>mdi-hand-back-right</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                3/3 Liike (LI) – Motorinen vaste
              </div>
              <div class="text-caption grey--text text--darken-2">
                Paras liikevaste yläraajoista (kehotus tai kipuärsyke):
              </div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in gcsCriteria.motor"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                motorScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                motorScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectMotor(opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    motorScore === opt.score ? 'primary' : 'grey lighten-3'
                  "
                  :class="
                    motorScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      motorScore === opt.score ? 'primary--text' : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="motorScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 4: TULOS JA TOIMENPITEET -->
        <div v-else-if="currentStep === 4">
          <!-- Big Score Banner -->
          <v-card
            flat
            :class="['pa-4 rounded-xl text-center mb-4', severityBgClass]"
          >
            <div
              class="text-caption text-uppercase font-weight-bold letter-spacing-1"
            >
              Glasgow Coma Scale -kokonaispisteet
            </div>
            <div
              class="text-h3 font-weight-black my-1"
              :class="severityTextClass"
            >
              {{ totalScore }} / 15
            </div>
            <v-chip
              :color="severityChipColor"
              text-color="white"
              class="font-weight-bold text-subtitle-2 px-4"
            >
              {{ severityTitle }}
            </v-chip>
            <div class="text-caption mt-2 grey--text text--darken-2 font-mono">
              Merkintä raporttiin:
              <strong
                >E{{ eyesScore }} V{{ verbalScore }} M{{ motorScore }}</strong
              >
            </div>
          </v-card>

          <!-- Component Breakdown Summary -->
          <div class="text-subtitle-2 font-weight-bold mb-2">
            Pisteytyksen erittely:
          </div>
          <v-row dense class="mb-3">
            <v-col cols="12" sm="4">
              <v-card
                outlined
                class="pa-2 rounded-lg text-center h-100 grey lighten-5 cursor-pointer"
                @click="currentStep = 1"
              >
                <div class="text-caption grey--text text--darken-1">
                  Silmät (SI)
                </div>
                <div class="text-subtitle-2 font-weight-bold primary--text">
                  {{ eyesScore }} / 4 p
                </div>
                <div class="text-caption text-truncate">
                  {{ currentEyesObj ? currentEyesObj.title : "-" }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card
                outlined
                class="pa-2 rounded-lg text-center h-100 grey lighten-5 cursor-pointer"
                @click="currentStep = 2"
              >
                <div class="text-caption grey--text text--darken-1">
                  Puhe (PU)
                </div>
                <div class="text-subtitle-2 font-weight-bold primary--text">
                  {{ verbalScore }} / 5 p
                </div>
                <div class="text-caption text-truncate">
                  {{ currentVerbalObj ? currentVerbalObj.title : "-" }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card
                outlined
                class="pa-2 rounded-lg text-center h-100 grey lighten-5 cursor-pointer"
                @click="currentStep = 3"
              >
                <div class="text-caption grey--text text--darken-1">
                  Liike (LI)
                </div>
                <div class="text-subtitle-2 font-weight-bold primary--text">
                  {{ motorScore }} / 6 p
                </div>
                <div class="text-caption text-truncate">
                  {{ currentMotorObj ? currentMotorObj.title : "-" }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Action Recommendations Based on Score -->
          <v-card
            outlined
            class="pa-3 rounded-lg mb-3"
            :class="severityBorderClass"
          >
            <div class="d-flex align-center mb-2">
              <v-icon :color="severityIconColor" class="mr-2">
                {{ severityIcon }}
              </v-icon>
              <div
                class="text-subtitle-2 font-weight-bold"
                :class="severityTextClass"
              >
                Ensivasteen toimintaohje tälle tajunnantasolle:
              </div>
            </div>

            <!-- GCS <= 8 (Critical) -->
            <div v-if="totalScore <= 8" class="text-body-2">
              <div class="font-weight-bold red--text text--darken-3 mb-1">
                🚨 VÄLITÖN ILMATIEUHKA (GCS &le; 8):
              </div>
              <ul class="pl-4 mb-2">
                <li>
                  <strong>Turvaa ilmatiet heti:</strong> Käännä kylkiasentoon
                  (ellei rankavammaepäilyä), avaa leuan nostolla ja aseta
                  nieluputki.
                </li>
                <li>
                  <strong>Ventilaatio:</strong> Jos hengitys on riittämätöntä
                  (HT &lt; 10/min tai agonaalista korinaa), avusta hengitystä
                  elvytyspalkeella (12–15 l/min O2).
                </li>
                <li>
                  <strong>Imuvalmius:</strong> Pidä mekaaninen imulaite käsillä
                  aspiraation varalta.
                </li>
                <li>
                  <strong>Konsultaatio:</strong> Varmista että lääkäriyksikkö
                  (ESA 00) tai kenttäjohtaja (L4) on hälytetty.
                </li>
              </ul>
            </div>

            <!-- GCS 9-12 (Moderate) -->
            <div v-else-if="totalScore <= 12" class="text-body-2">
              <div class="font-weight-bold orange--text text--darken-4 mb-1">
                ⚠️ KOHTALAINEN TAJUNNAN HÄIRIÖ (GCS 9–12):
              </div>
              <ul class="pl-4 mb-2">
                <li>
                  <strong>Jatkuva valvonta:</strong> GCS-lasku yli 2 pistettä on
                  hälyttävä merkki tilan nopeasta romahtamisesta.
                </li>
                <li>
                  <strong>Mittaa peruselintoiminnot:</strong> Verensokeri
                  (B-Gluk), SpO2, verenpaine (RR) ja hengitystiheys (HT).
                </li>
                <li>
                  <strong>Happihoito:</strong> Anna tarvittaessa lisähappea ja
                  tue turvallista asentoa.
                </li>
                <li>
                  <strong>Raportointi:</strong> Valmistaudu iSBAR-raporttiin
                  ambulanssille tai lääkärille.
                </li>
              </ul>
            </div>

            <!-- GCS 13-14 (Mild) -->
            <div v-else-if="totalScore <= 14" class="text-body-2">
              <div class="font-weight-bold amber--text text--darken-4 mb-1">
                ℹ️ LIEVÄ TAJUNNAN HÄIRIÖ (GCS 13–14):
              </div>
              <ul class="pl-4 mb-2">
                <li>
                  <strong>Selvitä syy:</strong> Mittaa verensokeri (B-Gluk)
                  heti, tarkista puolierot (FAST-testi: kasvot, kädet, puhe).
                </li>
                <li>
                  <strong>Huomioi:</strong> Mahdollinen pään vamma, päihteet,
                  lääkkeet, myrkytys tai infektio.
                </li>
                <li>
                  <strong>Seuraa kehitystä:</strong> Toista GCS-mittaus 5–10 min
                  välein.
                </li>
              </ul>
            </div>

            <!-- GCS 15 (Normal) -->
            <div v-else class="text-body-2">
              <div class="font-weight-bold green--text text--darken-3 mb-1">
                ✅ NORMAALI TAJUNNANTASO (GCS 15):
              </div>
              <ul class="pl-4 mb-2">
                <li>Potilas on orientoitunut ja vastaa asiallisesti.</li>
                <li>
                  Jatka järjestelmällistä tutkimusta (cABCDE) ja selvitä
                  hälytyksen perussyy.
                </li>
              </ul>
            </div>
          </v-card>
        </div>
      </v-card-text>

      <!-- Bottom Toolbar / Controls -->
      <v-divider />
      <v-card-actions class="pa-3 px-sm-4 grey lighten-5 d-flex align-center">
        <!-- Back button -->
        <v-btn
          v-if="currentStep > 1"
          text
          class="font-weight-bold primary--text text-none"
          @click="currentStep--"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Edellinen
        </v-btn>

        <v-btn
          text
          color="grey darken-1"
          class="text-none font-weight-bold"
          @click="resetWizard"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Nollaa
        </v-btn>

        <v-spacer />

        <!-- Next / Finish buttons -->
        <v-btn
          v-if="currentStep < 4"
          color="primary"
          depressed
          class="rounded-lg font-weight-bold text-none px-4"
          @click="currentStep++"
        >
          Seuraava
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>

        <v-btn
          v-else
          color="primary"
          depressed
          class="rounded-lg font-weight-bold text-none px-4"
          @click="$emit('input', false)"
        >
          Valmis (Sulje)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { GCS_CRITERIA } from "@/assets/ensivasteOhjeet";

export default {
  name: "GcsWizard",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initialEyes: {
      type: Number,
      default: 4,
    },
    initialVerbal: {
      type: Number,
      default: 5,
    },
    initialMotor: {
      type: Number,
      default: 6,
    },
  },
  data() {
    return {
      currentStep: 1,
      eyesScore: this.initialEyes,
      verbalScore: this.initialVerbal,
      motorScore: this.initialMotor,
      gcsCriteria: GCS_CRITERIA,
    };
  },
  computed: {
    totalScore() {
      const e = Number(this.eyesScore) || 0;
      const v = Number(this.verbalScore) || 0;
      const m = Number(this.motorScore) || 0;
      return e + v + m;
    },
    totalScoreText() {
      return `GCS: ${this.totalScore} / 15`;
    },
    isAllMax() {
      return (
        this.eyesScore === 4 && this.verbalScore === 5 && this.motorScore === 6
      );
    },
    steps() {
      return [
        { title: "Silmät", short: "1. Silmät", score: this.eyesScore },
        { title: "Puhe", short: "2. Puhe", score: this.verbalScore },
        { title: "Liike", short: "3. Liike", score: this.motorScore },
        { title: "Tulos", short: "4. Tulos", score: this.totalScore },
      ];
    },
    currentEyesObj() {
      return this.gcsCriteria.eyes.find((o) => o.score === this.eyesScore);
    },
    currentVerbalObj() {
      return this.gcsCriteria.verbal.find((o) => o.score === this.verbalScore);
    },
    currentMotorObj() {
      return this.gcsCriteria.motor.find((o) => o.score === this.motorScore);
    },
    severityTitle() {
      if (this.totalScore === 15) return "Normaali tajunnan taso";
      if (this.totalScore >= 13) return "Lievä tajunnan häiriö";
      if (this.totalScore >= 9) return "Kohtalainen tajunnan häiriö";
      return "Syvä tajuttomuus – Ilmatieuhka!";
    },
    severityChipColor() {
      if (this.totalScore === 15) return "green darken-1";
      if (this.totalScore >= 13) return "amber darken-2";
      if (this.totalScore >= 9) return "orange darken-3";
      return "red darken-3";
    },
    severityTextClass() {
      if (this.totalScore === 15) return "green--text text--darken-3";
      if (this.totalScore >= 13) return "amber--text text--darken-4";
      if (this.totalScore >= 9) return "orange--text text--darken-4";
      return "red--text text--darken-4";
    },
    severityBgClass() {
      if (this.totalScore === 15) return "green lighten-5";
      if (this.totalScore >= 13) return "amber lighten-5";
      if (this.totalScore >= 9) return "orange lighten-5";
      return "red lighten-5";
    },
    severityBorderClass() {
      if (this.totalScore === 15) return "green lighten-4";
      if (this.totalScore >= 13) return "amber lighten-4";
      if (this.totalScore >= 9) return "orange lighten-4";
      return "red lighten-4";
    },
    severityIcon() {
      if (this.totalScore === 15) return "mdi-check-circle";
      if (this.totalScore >= 13) return "mdi-information";
      if (this.totalScore >= 9) return "mdi-alert";
      return "mdi-alert-octagon";
    },
    severityIconColor() {
      if (this.totalScore === 15) return "green darken-1";
      if (this.totalScore >= 13) return "amber darken-2";
      if (this.totalScore >= 9) return "orange darken-3";
      return "red darken-3";
    },
  },
  watch: {
    value(val) {
      if (val) {
        // Reset step to 1 when dialog is opened
        this.currentStep = 1;
      }
    },
  },
  methods: {
    selectEyes(score) {
      this.eyesScore = score;
      // Auto-advance to step 2 after a brief moment
      setTimeout(() => {
        if (this.currentStep === 1) {
          this.currentStep = 2;
        }
      }, 150);
    },
    selectVerbal(score) {
      this.verbalScore = score;
      setTimeout(() => {
        if (this.currentStep === 2) {
          this.currentStep = 3;
        }
      }, 150);
    },
    selectMotor(score) {
      this.motorScore = score;
      setTimeout(() => {
        if (this.currentStep === 3) {
          this.currentStep = 4;
        }
      }, 150);
    },
    setAllMax() {
      this.eyesScore = 4;
      this.verbalScore = 5;
      this.motorScore = 6;
      this.currentStep = 4;
    },
    resetWizard() {
      this.eyesScore = 4;
      this.verbalScore = 5;
      this.motorScore = 6;
      this.currentStep = 1;
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.font-mono {
  font-family: monospace;
}
</style>
